package models

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlin.random.Random

/**
 * A mid-round Ginko interruption. Unlike [RoundModifier], these are short-lived beats that appear
 * after a round has already gotten tense.
 */
@Serializable(with = GinkoEventSerializer::class)
enum class GinkoEvent {
    NONE,

    /** The next roll also busts on a 6. */
    DANGER_ZONE,

    /** The next banking action steals a small cut from players who are still riding. */
    HEIST_WINDOW,

    /** The pot is hidden until someone banks or the round busts. */
    MYSTERY_SURGE,

    /** Players get one more chance to bank; skipping it locks banking until the turn comes around. */
    LAST_CALL,

    /** Banking is locked until the remaining riders each survive a locked turn. */
    ALL_IN,

    /** The next safe roll pays double. */
    HOT_STREAK;

    val consumesNextRoll: Boolean
        get() = this == DANGER_ZONE || this == HEIST_WINDOW || this == HOT_STREAK

    val locksBanking: Boolean
        get() = this == ALL_IN

    fun afterRollSurvives(activePlayerCount: Int, bankLockoutTurnsRemaining: Int): GinkoEventProgression = when (this) {
        LAST_CALL -> {
            val lockedTurns = (activePlayerCount - 1).coerceAtLeast(0)
            if (lockedTurns == 0) {
                GinkoEventProgression(NONE, 0)
            } else {
                GinkoEventProgression(ALL_IN, lockedTurns)
            }
        }
        ALL_IN -> {
            val lockedTurns = (bankLockoutTurnsRemaining - 1).coerceAtLeast(0)
            if (lockedTurns == 0) {
                GinkoEventProgression(NONE, 0)
            } else {
                GinkoEventProgression(ALL_IN, lockedTurns)
            }
        }
        DANGER_ZONE, HEIST_WINDOW, HOT_STREAK -> GinkoEventProgression(NONE, 0)
        else -> GinkoEventProgression(this, 0)
    }

    fun afterBank(): GinkoEvent = when (this) {
        HEIST_WINDOW, MYSTERY_SURGE -> NONE
        else -> this
    }

    companion object {
        const val HEIST_SKIM_POINTS = 10

        private val INTERRUPTIONS = listOf(
            DANGER_ZONE,
            HEIST_WINDOW,
            MYSTERY_SURGE,
            LAST_CALL,
            HOT_STREAK,
        )

        fun pick(random: Random = Random.Default): GinkoEvent =
            INTERRUPTIONS[random.nextInt(INTERRUPTIONS.size)]
    }
}

data class GinkoEventProgression(
    val event: GinkoEvent,
    val bankLockoutTurnsRemaining: Int,
)

object GinkoEventSerializer : KSerializer<GinkoEvent> {
    override val descriptor = PrimitiveSerialDescriptor("GinkoEvent", PrimitiveKind.STRING)

    override fun deserialize(decoder: Decoder): GinkoEvent {
        val value = decoder.decodeString()
        return GinkoEvent.entries.firstOrNull { it.name == value } ?: GinkoEvent.NONE
    }

    override fun serialize(encoder: Encoder, value: GinkoEvent) {
        encoder.encodeString(value.name)
    }
}
